import React from 'react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@shopflo/ui'
import { ChevronDown, MoreVertical, Eye, Edit, Trash2, Loader } from 'react-feather'
import GenericTable from 'src/components/common/table/GenericTable'
import { ColumnDef } from '@tanstack/react-table'
import RequestListBadge from './RequestListBadge'
import { FeedbackReponseType, FeatureStatus } from 'src/lib/types/support'
import { groupFeedbackByStatus } from 'src/lib/utils/support'
import { useSupportPageContext } from 'src/lib/contexts/SupportPageContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { ArrowClockwise } from '@phosphor-icons/react'
import { KeyedMutator } from 'swr'
import { ResyncButton } from './ResyncButton'

interface FeatureList {
  normalizedFeebackResponse: FeedbackReponseType[]
  mutateSuggestion: KeyedMutator<any>
  isLoading: boolean
}

type FeatureRow = FeedbackReponseType & { ticketStatus: FeatureStatus }

const FeatureList: React.FC<FeatureList> = ({ normalizedFeebackResponse, mutateSuggestion, isLoading }) => {
  const {
    actions: {
      handleFeatureSuggestion,
      handleFeatureSuggestionDialog,
      handleSetViewFeatureDialogOpen,
      handleSetDeleteTicketDialogOpen
    }
  } = useSupportPageContext()

  const { t } = useLocale()

  const parsedResponse = groupFeedbackByStatus(normalizedFeebackResponse)

  const columns: ColumnDef<FeatureRow>[] = [
    {
      accessorKey: 'issueId',
      header: 'Ticket number',
      size: 200,
      cell: ({ row }) => {
        return row.original.issueId.substring(4)
      }
    },
    {
      accessorKey: 'summary',
      header: 'Title',
      size: 200,
      cell: ({ row }) => {
        const summary = row.original.summary
        const shortenedSummary = summary.length > 30 ? summary.substring(0, 30) + '...' : summary
        return <p>{shortenedSummary}</p>
      }
    },
    {
      accessorKey: 'createdOn',
      header: 'Created',
      size: 200,
      cell: ({ row }) => <p>{row.original.createdOn}</p>
    },
    {
      accessorKey: 'comments',
      header: 'Comments',
      size: 200,
      cell: ({ row }) => {
        const comment = row.original.comments?.[0]?.text
        const isMultiLine = comment?.includes('\n')

        let displayedComment = comment

        if (isMultiLine) {
          displayedComment = comment.substring(0, comment.indexOf('\n'))
        }

        const shortenedComment =
          displayedComment?.length > 30
            ? displayedComment.substring(0, 30) + '...'
            : isMultiLine
            ? displayedComment + '...'
            : displayedComment

        return <p>{shortenedComment}</p>
      }
    },
    {
      accessorKey: 'lastUpdated',
      header: 'Updated',
      size: 200,
      cell: ({ row }) => <p>{row.original.lastUpdated}</p>
    },
    {
      id: 'actions',
      header: '',
      maxSize: 20,
      cell: ({ row }) => {
        const item = row.original
        const isDisabled = !['todo'].includes(item.ticketStatus)

        return (
          <Popover>
            <PopoverTrigger asChild>
              <div className='flex justify-end cursor-pointer z-50'>
                <MoreVertical size={16} />
              </div>
            </PopoverTrigger>
            <PopoverContent align='end' className='w-40 p-2 bg-white rounded-xl shadow-md'>
              <div className='flex flex-col gap-2 bg-white'>
                <button
                  className='flex items-center gap-2 text-sm font-medium px-3 py-2 hover:bg-gray-light rounded-md'
                  onClick={() => {
                    handleFeatureSuggestion(item)
                    handleSetViewFeatureDialogOpen(true)
                  }}
                >
                  <Eye size={16} />
                  <span>{t('billing.view')}</span>
                </button>
                <button
                  disabled={isDisabled}
                  className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-md ${
                    isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-light'
                  }`}
                  onClick={() => {
                    if (isDisabled) return
                    handleFeatureSuggestion(item)
                    handleFeatureSuggestionDialog(true)
                  }}
                >
                  <Edit size={16} />
                  <span>{t('edit')}</span>
                </button>
                <button
                  disabled={isDisabled}
                  className={`flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-md ${
                    isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-light'
                  }`}
                  onClick={() => {
                    if (isDisabled) return
                    handleFeatureSuggestion(item)
                    handleSetDeleteTicketDialogOpen(true)
                  }}
                >
                  <Trash2 size={16} />
                  <span>{t('delete')}</span>
                </button>
              </div>
            </PopoverContent>
          </Popover>
        )
      }
    }
  ]

  return (
    <div className='rounded-xl gap-4 flex flex-col'>
      {parsedResponse.map((section, index: number) => {
        const isOpenByDefault = section.isOpen ? `item-${index}` : undefined
        const hasData = section.data.length > 0

        return (
          <div key={index} className='bg-gray-lightest p-4 rounded-2xl'>
            {hasData ? (
              <Accordion type='single' collapsible defaultValue={isOpenByDefault} className='w-full'>
                <AccordionItem value={`item-${index}`}>
                  <AccordionTrigger>
                    <div className='flex justify-between w-full items-center'>
                      <div className='flex gap-2 items-center'>
                        <RequestListBadge type={section.status} count={section.count} />
                      </div>
                      <ResyncButton isLoading={isLoading} mutateSuggestion={mutateSuggestion} status={section.status} />
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <GenericTable
                      columns={columns}
                      data={section.data.map(item => ({
                        ...item,
                        ticketStatus: section.status
                      }))}
                      customPaginationConfig={{
                        customPagination: true
                      }}
                      isCustomizable={{
                        isFullWidth: false,
                        isResizable: true,
                        hidePaginationButton: true,
                        hideDropDown: true
                      }}
                    />
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            ) : (
              <div className='flex justify-between w-full items-center'>
                <div className='flex gap-2 items-center'>
                  <RequestListBadge type={section.status} count={section.count} />
                </div>
                <ResyncButton isLoading={isLoading} mutateSuggestion={mutateSuggestion} status={section.status} />
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

export default FeatureList
